import { useEffect, useRef } from 'react';
import { TURNSTILE_KEY } from '@/lib/env';

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        opts: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          appearance?: 'always' | 'execute' | 'interaction-only';
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptLoaded: Promise<void> | null = null;

function ensureScript(): Promise<void> {
  if (scriptLoaded) return scriptLoaded;
  scriptLoaded = new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    const existing = document.querySelector(`script[src^="${SCRIPT_SRC.split('?')[0]}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return scriptLoaded;
}

interface TurnstileProps {
  /** Called when the captcha is solved. The token is short-lived (~5 min). */
  onToken: (token: string) => void;
  /** Called when a previously-issued token expires. Use to clear local state. */
  onExpired?: () => void;
  /** Called when the widget itself errors out (network, blocked, etc.). */
  onError?: () => void;
  /**
   * 'always'  — show widget unconditionally (default)
   * 'execute' — invisible challenge (only triggered when you call .execute())
   * 'interaction-only' — only show if user interaction is required
   */
  appearance?: 'always' | 'execute' | 'interaction-only';
}

/**
 * Turnstile captcha widget. The handler refs below pin the *latest* prop
 * callbacks so the widget can safely render once on mount without
 * re-rendering whenever the parent passes a new closure — which would
 * otherwise reset a solved captcha state on every keystroke of an
 * adjacent input field.
 */
export function Turnstile({ onToken, onExpired, onError, appearance = 'always' }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  // Stable refs to latest callbacks so we can mount the widget exactly
  // once. Without these, an inline `onToken={(t) => setToken(t)}` parent
  // would re-render the widget on every parent render.
  const onTokenRef = useRef(onToken);
  const onExpiredRef = useRef(onExpired);
  const onErrorRef = useRef(onError);
  onTokenRef.current = onToken;
  onExpiredRef.current = onExpired;
  onErrorRef.current = onError;

  useEffect(() => {
    let cancelled = false;
    ensureScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        const id = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_KEY,
          callback: (token) => onTokenRef.current(token),
          'expired-callback': () => onExpiredRef.current?.(),
          'error-callback': () => onErrorRef.current?.(),
          theme: 'auto',
          appearance,
        });
        widgetIdRef.current = id;
      })
      .catch((err) => {
        console.error('Turnstile failed to load', err);
        onErrorRef.current?.();
      });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore
        }
      }
    };
    // appearance is the only "real" dep — callbacks are routed via refs.
  }, [appearance]);

  return <div ref={containerRef} className="mt-2" />;
}
